import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText } from "react-feather";
import { useHasPermission } from "../../auth/useClaims";
import { paths } from "../../../routes/paths";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetAmcRenewalsDueQuery } from "../../amc/amcApi";
import { money } from "../dashboardHelpers";
import DashboardSectionCard from "../components/DashboardSectionCard";
import { URGENCY, fmtDate, UrgencyStrip, UrgencySummary } from "./amcUrgency";

const INITIAL_ROWS = 6;

/**
 * AMC contracts needing renewal.
 *
 * This exists because a contract stays ACTIVE until somebody closes it - so a contract that
 * ended last March is not marked "expired" anywhere, it simply stops generating visits and
 * goes quiet. Nothing in the app surfaced that, which is the whole reason a renewal can be
 * missed. A row leaves this list the moment a contract is created against it as a renewal,
 * so acting on it is what clears it, not dismissing it.
 *
 * Visits done against visits sold is on every row deliberately: it is the number that
 * decides whether the renewal call is an easy conversation or a hard one, and it is the
 * first thing anyone would otherwise go and look up.
 */
const AmcRenewalsDueCard = () => {
  const canView = useHasPermission("Amc.ManageContracts");
  const { selectedBranchId } = useBranchFilter();
  const [showAll, setShowAll] = useState(false);

  const { data: rows = [], isFetching } = useGetAmcRenewalsDueQuery(
    { branchId: selectedBranchId ?? undefined },
    { skip: !canView }
  );

  if (!canView) return null;

  const visible = showAll ? rows : rows.slice(0, INITIAL_ROWS);
  const settings = rows[0];

  return (
    <div className="col-12">
      <DashboardSectionCard
        title="AMC - Renewals Pending"
        headerRight={
          settings
            ? `From ${settings.leadDays} days before expiry`
            : "Set the renewal reminder days in System Settings"
        }
        isLoading={isFetching}
        isEmpty={rows.length === 0}
        emptyText="No AMC contract is due for renewal, and none has expired unrenewed."
      >
        <UrgencySummary rows={rows} field="urgency" order={["EXPIRED", "EXPIRES_TODAY", "DUE_SOON"]} />

        <div className="d-flex flex-column gap-2">
          {visible.map((row) => (
            <RenewalRow key={row.contractRecordGuid} row={row} />
          ))}
        </div>

        {rows.length > INITIAL_ROWS && (
          <button type="button" className="btn btn-link btn-sm px-0 mt-2" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show less" : `Show all ${rows.length}`}
          </button>
        )}
      </DashboardSectionCard>
    </div>
  );
};

// Signed the way a deadline reads: positive is time left, negative is time already lost.
const expiryLabel = (days) => {
  if (days < 0) {
    const d = Math.abs(days);
    return { big: String(d), small: d === 1 ? "day expired" : "days expired" };
  }
  if (days === 0) return { big: "Ends", small: "today" };
  return { big: String(days), small: days === 1 ? "day left" : "days left" };
};

const RenewalRow = ({ row }) => {
  const band = URGENCY[row.urgency] || URGENCY.DUE_SOON;
  const label = expiryLabel(row.daysToExpiry);

  return (
    <div className="d-flex align-items-stretch" style={{ gap: 12 }}>
      <UrgencyStrip color={band.color} />

      <div className="flex-grow-1 py-1" style={{ minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-start" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate" style={{ fontSize: 15 }}>
              {row.customerName || `Customer ${row.custId}`}
            </div>
            <div className="text-muted small text-truncate">
              {row.contractNo} · {row.branchCode} · ends {fmtDate(row.endDate)}
              {row.amcAmount != null && ` · ${money(row.amcAmount)}`}
            </div>
            <div className="small text-truncate" style={{ color: band.color }}>
              {row.visitsDone} of {row.visitCount} visits done
              {row.visitsPending > 0 && ` · ${row.visitsPending} never carried out`}
              {row.lastServiceDate
                ? ` · last serviced ${fmtDate(row.lastServiceDate)}`
                : " · never serviced"}
            </div>
            {/* Renewing is done on the AMC page (a renewal is a NEW contract linked back
                to this one), so the row's job is to get the user there on the contract it
                is about instead of leaving them to search the register for it. */}
            <Link to={paths.amcRecord(row.contractRecordGuid)} className="btn btn-sm btn-link py-0 px-0 mt-1">
              <FileText size={12} className="me-1" /> Open AMC to renew
            </Link>
          </div>

          <div className="text-end flex-shrink-0">
            <div className="fw-bold lh-1" style={{ color: band.color, fontSize: 22 }}>
              {label.big}
            </div>
            <div className="small" style={{ color: band.color }}>{label.small}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmcRenewalsDueCard;
